import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string

const projectRef = new URL(supabaseUrl).hostname.split('.')[0]
const AUTH_STORAGE_KEY = `sb-${projectRef}-auth-token`
const LEGACY_COOKIE_CHUNKS = 32

function cookieValue(name: string): string | null {
  if (typeof document === 'undefined') return null
  const prefix = `${name}=`
  const match = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix))
  return match ? match.slice(prefix.length) : null
}

function readLegacyCookieSession(): string | null {
  const single = cookieValue(AUTH_STORAGE_KEY)
  if (single) return decodeURIComponent(single)

  let encoded = ''
  for (let index = 0; index < LEGACY_COOKIE_CHUNKS; index += 1) {
    const chunk = cookieValue(`${AUTH_STORAGE_KEY}.${index}`)
    if (!chunk) break
    encoded += chunk
  }
  return encoded ? decodeURIComponent(encoded) : null
}

function expireCookie(name: string) {
  if (typeof document === 'undefined') return
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''
  // Clear both the old shared-domain cookie and any older host-only variant.
  document.cookie = `${name}=; Path=/; SameSite=Lax; Max-Age=0${secure}`
  if (window.location.hostname.endsWith('veltica.com')) {
    document.cookie = `${name}=; Path=/; SameSite=Lax; Max-Age=0; Domain=.veltica.com${secure}`
  }
}

/**
 * One-time migration from the former `.veltica.com` cookie to origin-local
 * storage. Only the dashboard adopts the legacy session; the editor obtains a
 * separate session through the authenticated one-time handoff flow.
 */
function migrateLegacySession() {
  if (typeof window === 'undefined') return
  try {
    const legacy = readLegacyCookieSession()
    if (legacy && !window.localStorage.getItem(AUTH_STORAGE_KEY)) {
      window.localStorage.setItem(AUTH_STORAGE_KEY, legacy)
    }
  } catch {
    // Supabase will fall back to a clean login if storage is unavailable/corrupt.
  } finally {
    expireCookie(AUTH_STORAGE_KEY)
    for (let index = 0; index < LEGACY_COOKIE_CHUNKS; index += 1) {
      expireCookie(`${AUTH_STORAGE_KEY}.${index}`)
    }
  }
}

migrateLegacySession()

export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    storageKey: AUTH_STORAGE_KEY,
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
})
